<script setup lang="ts">
import IconPriceDropped from '@components/offer-card/icons/IconPriceDropped.vue';
import Typography from '@components/Typography.vue';
import { useLoading } from '@composables/useLoading.ts';
import { currencyNoDecimal } from '@elements/number-formatter';
import { getRecaptchaToken } from '@elements/recaptcha-helper';
import { resendInitPasswordEmail } from '@elements/resend-init-password-email';
import { showLoginModal } from '@elements/show-login-modal';
import { trans, transchoice } from '@elements/translation';
import { OfficeType } from '@enums/officeType.ts';
import { type OfferContactInitialAppState } from '@global-types/initialAppState.ts';
import { type SerializedOffer } from '@models/offer.ts';
import ContactForm from '@organisms/contact/ContactForm.vue';
import RealEstateSummary from '@organisms/contact/RealEstateSummary.vue';
import ReturnLink from '@organisms/contact/ReturnLink.vue';
import { PurchasedAfterSearch } from '@tools/algolia-events/PurchasedAfterSearch';
import { parseStringToDate } from '@tools/date';
import { eventBus } from '@tools/event-bus';
import { formatNumber } from '@tools/number';
import { Requests } from '@tools/requests';
import { URL_OFFER_CONTACT, URL_OFFER_CONTACT_RESEND_EMAIL, URL_OFFER_FAST_CONTACT_COMPLETION } from '@tools/routes/routes';
import { type AxiosResponse, isAxiosError } from 'axios';
import { computed, nextTick, onMounted, ref } from 'vue';
import { OBFUSCATE_ATTRIBUTE_NAME, obfuscateHref } from '../../obfuscate';
import OfferContactPromises from './components/OfferContactPromises.vue';
import PrimaryContactSummary from './components/PrimaryContactSummary.vue';

const { isLoading, startLoading, stopLoading } = useLoading();

const initialAppState = ref<OfferContactInitialAppState>(window.offerContactInitialAppState);

const offer = computed<SerializedOffer>(() => initialAppState.value.offer);
const sellerPartnershipDate = computed<Date | null>(() => {
  if (!initialAppState.value.sellerPartnershipDate) {
    return null;
  }

  return parseStringToDate(initialAppState.value.sellerPartnershipDate);
});
const sellerAverageResponseDelay = computed<number>(() => initialAppState.value.sellerAverageResponseDelay);

const offerHashIds = computed<string[]>(() => initialAppState.value.offerHashIds);

const isPriceDropRecent = computed<boolean>(() => offer.value.is_price_drop_recent);
const isOpenSpace = computed<boolean>(() => offer.value.office_type?.code === OfficeType.OPEN_SPACE);
const isAggregate = computed<boolean>(() => offerHashIds.value.length > 1);
const isUniqueOpenSpace = computed<boolean>(() => !isAggregate.value && isOpenSpace.value);

const capacity = computed<number>(() => (isAggregate.value ? initialAppState.value.totalCapacity : offer.value.capacity));
const totalPrice = computed<number>(() => initialAppState.value.totalPrice);
const totalSurface = computed<number>(() => initialAppState.value.totalSurface);

const recaptchaPrivacyUrl = computed<string>(() => window.recaptcha_privacy_url);
const recaptchaTermsUrl = computed<string>(() => window.recaptcha_terms_url);

const errorMessage = ref<string | null>(null);

const formattedCapacity = computed<string>(() => formatNumber(capacity.value));
const formattedSurface = computed<string>(() => formatNumber(totalSurface.value));

interface ContactFormData {
  firstname: string,
  lastname: string,
  email: string,
  message: string | null,
  phoneNumber: string,
}

const handleSubmit = async ({ form }: { form: ContactFormData }): Promise<void> => {
  startLoading();

  try {
    const recaptchaToken: string = await getRecaptchaToken(window.recaptcha);

    const response: AxiosResponse<{
      contactFunnelType: string,
      dealId: string,
    }> = await Requests.post(URL_OFFER_CONTACT.replace('{hashId}', offer.value.hash_id), {
      ...form,
      offerHashIds: initialAppState.value.offerHashIds,
      splitTeamSizes: initialAppState.value.splitTeamSizes,
      buyerTeamSize: initialAppState.value.buyerTeamSize,
      proposalId: initialAppState.value.proposalId,
      queryID: sessionStorage.getItem('queryID'),
      'g-recaptcha-token': recaptchaToken,
      isMatchProposal: !!(new URLSearchParams(window.location.search)).get('proposal_id'),
    });

    const purchasedEvent = new PurchasedAfterSearch(initialAppState.value.offerHashIds);
    await purchasedEvent.send();

    window.location.href = URL_OFFER_FAST_CONTACT_COMPLETION
      .replace('{hashId}', offer.value.hash_id)
      .replace('{contactFunnelType}', response.data.contactFunnelType)
      .replace('{dealId}', response.data.dealId);
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      errorMessage.value = e.response?.data.error;

      await nextTick();
      resendInitPasswordEmail('.offer-contact-guest-user-email-resend', URL_OFFER_CONTACT_RESEND_EMAIL);
      showLoginModal('.contact-login-button', offer.value.public_url);
      window.scrollTo(0, 0);
    }
  }

  stopLoading();
};

onMounted(() => eventBus.dispatchEvent(new CustomEvent('deobfuscate-links')));
</script>

<template>
  <div class="page-contact-form offer-contact container">
    <div class="offer-contact__header">
      <ReturnLink
        :link="offer.public_url"
        class="offer-contact__return-link"
      />

      <h1 class="offer-contact__title">
        {{ trans('offer.contact.title') }}
      </h1>
    </div>

    <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
    <Typography
      v-if="errorMessage"
      class="offer-contact__error"
      variant="body-small"
      color="red"
      v-html="errorMessage"
    />
    <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->

    <div class="offer-contact__content">
      <div class="offer-contact__info">
        <div class="offer-contact__summary">
          <RealEstateSummary
            :title="isAggregate ?
              transchoice('offer.contact.summary.title.aggregate_offices', { capacity: offerHashIds.length }) :
              offer.type_infos"
            :media="offer.cover"
          >
            <Typography
              variant="body-small"
              color="gray-darker"
            >
              {{ formattedCapacity }}

              {{ transchoice('global.post_office', { count: capacity }) }}

              <template v-if="isOpenSpace">
                {{ transchoice('global.available', { count: capacity }) }}
              </template>

              <template v-else>
                • {{ formattedSurface }} {{ trans('global.area_unit.MTK') }}
              </template>
            </Typography>

            <div class="offer-contact__price">
              <Typography
                variant="body-bold"
                :color="isPriceDropRecent ? 'green' : 'black'"
                class="offer-contact__price__value"
              >
                {{ currencyNoDecimal(isUniqueOpenSpace ? offer.price : totalPrice) }}

                <IconPriceDropped
                  v-if="isPriceDropRecent"
                  :height="16"
                  :width="16"
                />
              </Typography>

              <Typography
                variant="body-small"
                color="gray-darker"
              >
                {{ trans('global.price_tax') }} {{ trans('global.by.word') }}

                <template v-if="isUniqueOpenSpace">
                  {{ trans('offer.post_price') }} {{ trans('global.by.word') }} {{ trans('global.duration.month') }}
                </template>

                <template v-else>
                  {{ trans('global.duration.month') }}
                </template>
              </Typography>
            </div>
          </RealEstateSummary>

          <hr class="offer-contact__divider">

          <PrimaryContactSummary
            :primary-contact="offer.primary_contact"
            :partnership-date="sellerPartnershipDate"
            :average-response-delay="sellerAverageResponseDelay"
          />
        </div>

        <div class="offer-contact__promises offer-contact__promises--desktop">
          <OfferContactPromises />
        </div>
      </div>

      <div class="offer-contact__form-container">
        <ContactForm
          class="offer-contact__form"
          :is-loading="isLoading"
          with-message
          :message-field-label="transchoice('contact_form.field.message.label', { firstname: offer.primary_contact.firstname })"
          :submit-label="trans('offer.contact.submit.label')"
          @submit="handleSubmit"
        />

        <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component -->
        <Typography
          class="offer-contact__policy"
          variant="caption"
          color="gray-dark"
          v-html="transchoice('offer.contact.policy', {
            privacy_link_attribute: obfuscateHref(recaptchaPrivacyUrl),
            terms_link_attribute: obfuscateHref(recaptchaTermsUrl),
            obfuscate_attribute_name: OBFUSCATE_ATTRIBUTE_NAME,
            link_cgu: initialAppState.linkCgu,
            link_poc: initialAppState.linkPoc,
          })"
        />
        <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component -->
      </div>

      <div class="offer-contact__promises offer-contact__promises--mobile">
        <OfferContactPromises />
      </div>
    </div>
  </div>
</template>
